import api, { ApiResponse } from './axiosInstance';

export interface EpcDepartmentMaster {
  id?: string;
  _id?: string;
  name: string;
  code?: string;
  focusAreas?: string[];
  sortOrder?: number;
  inactive?: boolean;
  /** Distinct projects with an active org unit linked to this master. */
  usageCount?: number;
}

export function masterId(m: EpcDepartmentMaster): string {
  return String(m.id ?? m._id ?? '');
}

const base = '/epc/department-masters';

const epcDepartmentMasterService = {
  async list(includeInactive = false): Promise<EpcDepartmentMaster[]> {
    const res = await api.get<ApiResponse<EpcDepartmentMaster[]>>(base, {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return res.data.data ?? [];
  },

  /**
   * Create a master entry. On 409 (name already exists) resolves with the
   * existing entry id so callers can auto-select instead of failing.
   *
   * Note: the axios response interceptor rejects with `new Error(message)`
   * but re-attaches the original `response` object, so `.response.status`
   * and the error envelope's `data.existingMasterId` survive to here.
   */
  async create(body: { name: string; focusAreas?: string[] }): Promise<{ id: string; existed: boolean }> {
    try {
      const res = await api.post<ApiResponse<EpcDepartmentMaster>>(base, body);
      return { id: masterId(res.data.data as EpcDepartmentMaster), existed: false };
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { data?: { existingMasterId?: string } } } })
        .response;
      if (resp?.status === 409 && resp.data?.data?.existingMasterId) {
        return { id: resp.data.data.existingMasterId, existed: true };
      }
      throw err;
    }
  },

  async update(
    id: string,
    body: { name?: string; code?: string; focusAreas?: string[]; sortOrder?: number; inactive?: boolean },
  ): Promise<EpcDepartmentMaster> {
    const res = await api.patch<ApiResponse<EpcDepartmentMaster>>(`${base}/${id}`, body);
    return res.data.data as EpcDepartmentMaster;
  },

  async merge(sourceId: string, targetId: string): Promise<{ repointedOrgUnits: number; conflicts: string[] }> {
    const res = await api.post<ApiResponse<{ repointedOrgUnits: number; conflicts: string[] }>>(
      `${base}/${sourceId}/merge`,
      { targetId },
    );
    return res.data.data as { repointedOrgUnits: number; conflicts: string[] };
  },
};

export default epcDepartmentMasterService;
